import { ZfBox, ZfCard, ZfCardMedia, ZfCardMediaFooter, ZfCardMediaHeader, ZfIcon, ZfPill, ZfStack, ZfTypography } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { getImageUrlByString } from '../../../styles/assets/imageMap';
import { useClient, useTopics } from '../../api';
import { useYwuApp } from '../../contexts/ywu-app-context';
import { getMaxTileWidth, getTileHeight } from '../../utils/topicUtils';
import { getZfLifeEventIconForTile } from '../icons/iconUtils';
import { UpcomingEventList } from './UpcomingEventList';

interface IProps {
    showUpcomingEvents?: boolean;
    width: number;
    height: number;
}

export const TopicTiles: FunctionComponent<IProps> = ({ showUpcomingEvents, height, width }) => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { isPresentationMode } = useYwuApp();
    const { data: client } = useClient();
    const { data: topics } = useTopics();
    const pinnedTopicIds = client?.talkingPointPins.map((pin) => pin.topicId) ?? [];

    if (!topics) return null;

    const getGridRow = (index: number) => {
        const offset = isPresentationMode ? 2 : 1;
        if (index > 2) {
            return 2 + offset;
        } else {
            return 1 + offset;
        }
    };

    return (
        <>
            {showUpcomingEvents ? (
                <ZfBox
                    style={{
                        height: '100%',
                        minWidth: 0,
                        gridColumn: 1,
                        gridRow: isPresentationMode ? 3 : 2,
                        borderRadius: '3px',
                        display: 'flex',
                        overflow: 'hidden',
                        background: 'var(--bg-color-default-normal)',
                        boxShadow: '0px 2px 4px 0px rgba(143, 145, 156, 0.30)',
                    }}
                >
                    <UpcomingEventList />
                </ZfBox>
            ) : (
                <ZfBox
                    style={{
                        height: 'calc(100% - var(--padding-vertical-xxl))',
                        minWidth: 0,
                        gridColumn: 1,
                        gridRow: 2,
                        borderRadius: '3px',
                        display: 'flex',
                        overflow: 'hidden',
                        padding: 'var(--padding-vertical-md)',
                        backgroundColor: 'var(--bg-color-action-normal)',
                    }}
                >
                    <span style={{ color: 'var(--fg-color-default-invert)' }}>{t('welcome')}</span>
                </ZfBox>
            )}
            {topics.map((topic, i) => (
                <ZfBox className="user-dashboard__topic-tile" style={{ gridRow: getGridRow(i) }} key={i}>
                    <ZfCard onClick={() => navigate(topic.id.toString())}>
                        <ZfCardMedia
                            image={getImageUrlByString(topic.key)}
                            style={{
                                width: `${getMaxTileWidth(width)}px`,
                                height: `${getTileHeight(height, isPresentationMode)}px`,
                            }}
                        >
                            <ZfCardMediaHeader>
                                <ZfTypography slot="leading" color="invert">
                                    <ZfStack direction="row" spacing="extra-small" alignment="center">
                                        <ZfIcon name={getZfLifeEventIconForTile(topic.key ?? '')} size="extra-large" />
                                        <ZfTypography variant="body-large">{topic.name[i18n.language]}</ZfTypography>
                                    </ZfStack>
                                </ZfTypography>
                            </ZfCardMediaHeader>
                            {pinnedTopicIds.includes(topic.id) && (
                                <ZfCardMediaFooter>
                                    <ZfPill slot="leading" color="success" clickable={false}>
                                        {t('activeTopicTile')}
                                    </ZfPill>
                                </ZfCardMediaFooter>
                            )}
                        </ZfCardMedia>
                    </ZfCard>
                </ZfBox>
            ))}
        </>
    );
};
